/**
 * CRM Utility Functions
 * Helper functions for badges, dates, calculations
 */

import { formatDistance, parseISO, format, differenceInDays } from 'date-fns';
import { vi } from 'date-fns/locale';
import type { CRMStakeholder, CRMInteraction, InteractionType, CRMFollowUp } from './types';

// Badge colors for interaction types
export const getInteractionTypeColor = (type: InteractionType) => {
  const colors = {
    Meeting: 'bg-blue-50 text-blue-700 border-blue-500 dark:bg-blue-950 dark:text-blue-300',
    Call: 'bg-green-50 text-green-700 border-green-500 dark:bg-green-950 dark:text-green-300',
    'Site Visit': 'bg-orange-50 text-orange-700 border-orange-500 dark:bg-orange-950 dark:text-orange-300',
    Delivery: 'bg-purple-50 text-purple-700 border-purple-500 dark:bg-purple-950 dark:text-purple-300',
    Approval: 'bg-red-50 text-red-700 border-red-500 dark:bg-red-950 dark:text-red-300',
  };
  return colors[type];
};

// Event type colors for unified calendar events
export const getEventTypeColor = (eventType: 'follow-up' | 'interaction', interactionType?: InteractionType) => {
  if (eventType === 'follow-up') {
    return 'bg-orange-50 text-orange-700 border-orange-300 dark:bg-orange-950 dark:text-orange-300';
  }
  
  if (interactionType) {
    return getInteractionTypeColor(interactionType);
  }
  
  return 'bg-muted text-muted-foreground';
};

// Follow-up badge variant
export const getFollowUpBadgeVariant = (dateStr?: string): 'destructive' | 'default' | 'secondary' | 'outline' => {
  if (!dateStr) return 'outline';
  const days = differenceInDays(parseISO(dateStr), new Date());
  if (days < 0) return 'destructive'; // Overdue
  if (days === 0) return 'default'; // Today
  return 'secondary'; // Upcoming
};

// Activity status calculation
export const getActivityStatus = (lastContact?: string) => {
  if (!lastContact) return { label: 'Chưa liên hệ', color: 'text-gray-500', progress: 0 };
  
  const days = differenceInDays(new Date(), parseISO(lastContact));
  if (days <= 7) return { label: 'Tốt', color: 'text-green-600', progress: 100 };
  if (days <= 14) return { label: 'Bình thường', color: 'text-yellow-600', progress: 60 };
  return { label: 'Cần chú ý', color: 'text-red-600', progress: 30 };
};

// Format relative time in Vietnamese
export const formatRelativeTime = (dateStr: string) => {
  return formatDistance(parseISO(dateStr), new Date(), { 
    addSuffix: true, 
    locale: vi 
  });
};

// Format date for display
export const formatDateDisplay = (dateStr: string) => {
  return format(parseISO(dateStr), 'dd/MM/yyyy', { locale: vi });
};

// Format datetime for display
export const formatDateTimeDisplay = (dateStr: string) => {
  return format(parseISO(dateStr), 'dd/MM/yyyy HH:mm', { locale: vi });
};

// Generate user initials for avatar
export const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Calculate dashboard metrics
export const calculateMetrics = (
  stakeholders: CRMStakeholder[],
  interactions: CRMInteraction[],
  followUps: CRMFollowUp[]
) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  const overdueCount = stakeholders.filter(s => {
    if (!s.nextFollowUp) return false;
    return parseISO(s.nextFollowUp) < today;
  }).length;
  
  const todayCount = stakeholders.filter(s => {
    if (!s.nextFollowUp) return false;
    const followUpDate = parseISO(s.nextFollowUp);
    followUpDate.setHours(0, 0, 0, 0);
    return followUpDate.getTime() === today.getTime();
  }).length;
  
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - today.getDay());
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 7);
  
  const weekCount = interactions.filter(i => {
    const interactionDate = parseISO(i.date);
    return interactionDate >= weekStart && interactionDate < weekEnd;
  }).length;
  
  // Follow-up completion rate
  const completedFollowUps = followUps.filter(f => f.status === 'completed').length;
  const totalFollowUps = followUps.length;
  const followUpCompletionRate = totalFollowUps > 0 
    ? Math.round((completedFollowUps / totalFollowUps) * 100) 
    : 0;

  // Stakeholders needing attention (no interaction in 30 days)
  const stakeholderIds = new Set(
    interactions
      .filter(i => parseISO(i.date) >= thirtyDaysAgo)
      .map(i => i.stakeholder_id)
  );
  const inactiveStakeholders = stakeholders.filter(s => !stakeholderIds.has(s.id)).length;
  
  return {
    totalStakeholders: stakeholders.length,
    overdueFollowUps: overdueCount,
    todayFollowUps: todayCount,
    weekInteractions: weekCount,
    followUpCompletionRate,
    inactiveStakeholders,
  };
};

// Get role distribution for pie chart
export const getRoleDistribution = (stakeholders: CRMStakeholder[]) => {
  const distribution: Record<string, number> = {};
  stakeholders.forEach(s => {
    distribution[s.role] = (distribution[s.role] || 0) + 1;
  });
  return Object.entries(distribution).map(([name, value]) => ({ name, value }));
};

// Get interaction type distribution for bar chart
export const getInteractionDistribution = (interactions: CRMInteraction[]) => {
  const distribution: Record<string, number> = {};
  interactions.forEach(i => {
    distribution[i.type] = (distribution[i.type] || 0) + 1;
  });
  return Object.entries(distribution).map(([name, value]) => ({ name, value }));
};
